import React from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { formatDate } from '../../dashboard/components/dateUtils';
import { parseRawFormData } from '../../dashboard/components/formUtils';
import { HandlersCtpResponse } from '../../app/athenaApiGen';
import {
  WorkflowProgressValue,
  getWorkflowProgress,
} from '../../utils/workflow';

// Extended type to handle rawFormData parsing
type CTPApiItem = HandlersCtpResponse & {
  rawFormData?: string;
  workflowState?: string;
  workflowProgress?: WorkflowProgressValue;
};

export interface CTPRowData {
  id: string;
  ctpNumber: string;
  ctpDate: string | null;
  projectName: string;
  subject: string;
  contractorName: string;
  proposedSubcontractor: string;
  workflowState: string | null;
  workflowProgress: WorkflowProgressValue;
  workflowId: string | null;
}

const FALLBACK_TEXT = '--';

/**
 * Returns the first non-empty string value in the provided list.
 */
const pickFirstString = (
  ...values: Array<string | null | undefined>
): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value;
  }
  return undefined;
};

export const createCTPColumns = (): GridColDef<CTPRowData>[] => [
  { field: 'ctpNumber', headerName: 'CTP #', minWidth: 140, flex: 0.6 },
  {
    field: 'ctpDate',
    headerName: 'CTP Date',
    minWidth: 140,
    renderCell: (params) => formatDate(params?.value),
    sortable: false,
    flex: 0.5,
  },
  { field: 'projectName', headerName: 'Project', minWidth: 220, flex: 1 },
  { field: 'subject', headerName: 'Subject', minWidth: 200, flex: 1 },
  { field: 'contractorName', headerName: 'Contractor', minWidth: 180, flex: 0.8 },
  { field: 'proposedSubcontractor', headerName: 'Subcontractor', minWidth: 180, flex: 0.8 },
  {
    field: 'workflowState',
    headerName: 'Status',
    minWidth: 200,
    flex: 0.9,
    sortable: false,
    valueGetter: (value: string) => value ?? '-',
  },
];

export const mapCTPApiDataToRows = (items: CTPApiItem[]): CTPRowData[] => {
  return items.map((it) => {
    const raw = parseRawFormData(it.rawFormData);
    const workflowState =
      pickFirstString(
        it.workflowState as string | undefined,
        raw.workflowState,
        raw?.workflow?.state,
        raw?.workflowStateLabel,
      ) || null;
    const workflowProgress =
      (it.workflowProgress as WorkflowProgressValue | undefined) ??
      (raw.workflowProgress as WorkflowProgressValue | undefined) ??
      getWorkflowProgress(workflowState ?? undefined);
    return {
      id: it.ctpId ?? '',
      ctpNumber: pickFirstString(it.ctpNumber, raw?.ctpNumber) ?? FALLBACK_TEXT,
      ctpDate: pickFirstString(it.ctpDate, raw?.ctpDate) || null,
      projectName: pickFirstString(it.projectName, raw?.projectName) ?? FALLBACK_TEXT,
      subject: pickFirstString(it.subject, raw?.subject) ?? FALLBACK_TEXT,
      contractorName: pickFirstString(it.contractorName, raw?.contractorName) ?? FALLBACK_TEXT,
      proposedSubcontractor:
        pickFirstString(it.proposedSubcontractor, raw?.proposedSubcontractor) ?? FALLBACK_TEXT,
      workflowState,
      workflowProgress,
      workflowId: it.workflowId ?? null,
    };
  });
};
